<script>
    import { mapActions } from "vuex";
    import { apiUrl } from "@/utils";
    import { sendAnalytics } from "@/core/SendAnalytics";
    import BaseButton from "@main/packages/BaseButton";
    import BasePrice from "@main/packages/BasePrice";
    import LiteCompareIcon from "./LiteCompareIcon";
    import LiteCompareControls from "./LiteCompareControls";
    import LiteCompareColors from "./LiteCompareColors";

    export default {
        name: "LiteCompareMobileProduct",
        components: {
            LiteCompareControls,
            LiteCompareIcon,
            LiteCompareColors,
            BaseButton,
            BasePrice,
        },
        props: {
            product: {
                type: Object,
                required: true,
            },
            specifications: {
                type: Array,
                required: true,
            },
            inCartList: Array,
            position: Number,
            productsLength: Number,
            isFirst: Boolean,
            firstCardPosition: Number,
            secondCardPosition: Number,
            imagesList: {
                type: Object,
                default: () => ({}),
            },
            showColorsList: Boolean,
            colorsListSize: String,
        },
        data() {
            return {
                isLoadingBuyButton: false,
            };
        },
        computed: {
            productInCart() {
                return this.inCartList.includes(this.product.offers[0].id.toString());
            },
            productName() {
                return this.product.shortName || this.product.name;
            },
            productImage() {
                const staticImage = this.imagesList[this.product.slug];
                if (staticImage) {
                    return `/static/assets/lite-compare/${this.$_name}/products/${staticImage}?v=270522`;
                }
                return apiUrl(this.product.offers[0]?.previewImage?.medium);
            },
            buttonText() {
                return this.productInCart ? "В корзине" : "Купить";
            },
            filteredSpecifications() {
                return this.specifications.map((spec) => {
                    return {
                        name: spec.name,
                        values: spec.values ? spec.values[this.position] : null,
                        icons: spec.icons ? spec.icons[this.position] : null,
                        isSingle: spec.isSingle,
                    };
                });
            },
        },
        methods: {
            ...mapActions("Root", ["addProductInCart"]),
            addToCard() {
                if (this.productInCart) {
                    return this.$router.push({ name: "Order" });
                }

                this.isLoadingBuyButton = true;

                this.addProductInCart({
                    quantity: 1,
                    productId: this.product.offers[0].id,
                }).then(() => {
                    this.isLoadingBuyButton = false;

                    sendAnalytics.send(sendAnalytics.events.addToCart, {
                        name: this.product.name,
                        offerId: this.product.offers[0].id,
                        price: this.product.price,
                    });
                });
            },
            onClickArrow(position) {
                this.$emit("on-click-arrow", position);
            },
        },
    };
</script>

<template>
    <div class="lite-compare-mobile-product">
        <router-link
            :to="{ name: 'Product', params: { slug: product.slug } }"
            class="lite-compare-mobile-product__link"
        >
            <div class="lite-compare-mobile-product__image">
                <img :src="productImage" alt="" />
            </div>

            <div class="lite-compare-mobile-product__name">
                {{ productName }}
            </div>

            <div class="lite-compare-mobile-product__price">
                <base-price :price="product.discountPrice"></base-price>
            </div>
        </router-link>

        <base-button
            class="lite-compare-mobile-product__button"
            variant="v1"
            size="md"
            :is-loading="isLoadingBuyButton"
            :is-selected="productInCart"
            @click="addToCard"
        >
            {{ buttonText }}
            <preloader slot="preloader" :show="isLoadingBuyButton" sm-size></preloader>
        </base-button>

        <div v-if="productsLength > 2" class="lite-compare-mobile-product__arrows">
            <lite-compare-controls
                :max="productsLength"
                :current="isFirst ? firstCardPosition : secondCardPosition"
                :size="24"
                @on-click="onClickArrow"
            ></lite-compare-controls>
        </div>

        <div class="lite-compare-mobile-product__table">
            <div v-if="showColorsList" class="lite-compare-mobile-product__list">
                <lite-compare-colors
                    :size-type="colorsListSize"
                    :offers="product.offers"
                    :current-offer="product.offers[0]"
                ></lite-compare-colors>
            </div>

            <div
                class="lite-compare-mobile-product__list"
                v-for="(specification, specificationIndex) in filteredSpecifications"
                :key="specificationIndex"
            >
                <div
                    class="lite-compare-mobile-product__title"
                    :class="{ 'is-hidden': !isFirst }"
                >
                    {{ specification.name }}
                </div>
                <div
                    v-if="specification.values"
                    class="lite-compare-mobile-product__item"
                    :class="{
                        'is-single': isFirst && specification.isSingle,
                        'is-hidden': !isFirst && specification.isSingle,
                    }"
                >
                    <template v-if="specification.values.icons">
                        {{ specification.values.name }}
                        <lite-compare-icon
                            :icons="specification.values.icons"
                        ></lite-compare-icon>
                    </template>
                    <template v-else>{{ specification.values }}</template>
                </div>

                <div v-if="specification.icons" class="lite-compare-mobile-product__item">
                    <lite-compare-icon
                        :icons="specification.icons"
                        :position-x="isFirst ? 'left' : 'right'"
                    ></lite-compare-icon>
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="scss">
    .lite-compare-mobile-product {
        width: 50%;
    }

    .lite-compare-mobile-product__link {
        display: flex;
        flex-direction: column;
        align-items: center;
        &:hover {
            .lite-compare-mobile-product__name {
                color: var(--secondary);
            }
        }
    }

    .lite-compare-mobile-product__image {
        position: relative;
        width: 90%;
        img {
            position: absolute;
            top: 0;
            width: 100%;
            height: 100%;
            object-fit: cover;
            margin: 0 auto;
        }
        &::before {
            content: "";
            display: block;
            padding-bottom: var(--lite-compare-product-image-ratio);
        }
        @include bp($bp-desktop-sm) {
            width: var(--lite-compare-product-image-width);
            margin: 0 auto;
        }
    }

    .lite-compare-mobile-product__name {
        margin-bottom: 8px;
        color: var(--lite-compare-name-color);
        font-size: var(--lite-compare-name-size);
        font-weight: var(--lite-compare-name-weight);
        transition: color 0.2s ease;
    }

    .lite-compare-mobile-product__price {
        margin-bottom: 16px;
        color: var(--lite-compare-price-color);
        font-size: var(--lite-compare-price-size);
        font-weight: var(--lite-compare-price-weight);
    }

    .lite-compare-mobile-product__button {
        width: 120px;
        max-width: 180px;
        margin: 0 auto;
    }

    .lite-compare-mobile-product__arrows {
        margin-top: 24px;
    }

    .lite-compare-mobile-product__table {
        padding-top: 20px;
    }

    .lite-compare-mobile-product__title {
        position: relative;
        top: 0;
        left: calc(100% - 128px);
        margin-bottom: 12px;
        width: 280px;
        color: var(--lite-compare-table-cell-color--first);
        font-size: var(--lite-compare-table-cell-size--first);
        text-align: center;
        &.is-hidden {
            opacity: 0;
        }
    }

    .lite-compare-mobile-product__list {
        padding: 12px 6px 16px;
        &:not(:last-child) {
            border-bottom: 1px solid var(--lite-compare-table-row-border-color);
        }
    }

    .lite-compare-mobile-product__item {
        display: flex;
        justify-content: center;
        align-items: center;
        min-height: 50px;
        color: var(--lite-compare-table-cell-color);
        font-size: var(--lite-compare-table-cell-size);
        font-weight: var(--lite-compare-table-cell-weight);
        text-align: center;
        &.is-single {
            position: relative;
            left: calc(100% - 130px);
            width: 260px;
        }
        &.is-hidden {
            display: none;
        }
    }
</style>
